/**
 * MoveFolderModal — pick a destination folder from a simplified tree.
 */

import { useState } from 'react'
import { X, ChevronRight, ChevronDown, Folder } from 'lucide-react'
import type { FolderNode } from '../../types/fileManager'

interface MoveFolderModalProps {
  title: string
  tree: FolderNode[]
  excludeIds?: Set<number>
  onClose: () => void
  onSelect: (folderId: number) => Promise<void>
}

function TreeRow({
  node,
  depth,
  selectedId,
  excludeIds,
  onSelect,
}: {
  node: FolderNode
  depth: number
  selectedId: number | null
  excludeIds: Set<number>
  onSelect: (id: number) => void
}) {
  const [expanded, setExpanded] = useState(depth < 2)
  const hasChildren = node.children && node.children.length > 0
  const isExcluded = excludeIds.has(node.id)
  // Only allow drops into leaf folders or folders that can accept content
  const isLeaf = node.folder_type === 'source_bucket' || node.folder_type === 'custom'

  return (
    <div>
      <button
        disabled={isExcluded}
        onClick={() => isLeaf ? onSelect(node.id) : setExpanded(!expanded)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
          isExcluded
            ? 'opacity-30 cursor-not-allowed'
            : selectedId === node.id
              ? 'bg-[var(--rq-blue)]/20 text-[var(--rq-text)] border border-[var(--rq-blue)]/40'
              : 'hover:bg-white/5 text-[var(--rq-text-soft)]'
        }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
        ) : (
          <span className="w-3.5" />
        )}
        <Folder className={`w-4 h-4 flex-shrink-0 ${
          node.folder_type === 'date_bucket' ? 'text-[var(--rq-amber)]' :
          node.folder_type === 'source_bucket' ? 'text-[var(--rq-blue)]' :
          'text-[var(--rq-text-soft)]'
        }`} />
        <span className="truncate">{node.name}</span>
        {isLeaf && <span className="ml-auto text-xs text-[var(--rq-text-soft)]">{node.asset_count}</span>}
      </button>
      {expanded && hasChildren && node.children.map(child => (
        <TreeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          excludeIds={excludeIds}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

export default function MoveFolderModal({ title, tree, excludeIds = new Set(), onClose, onSelect }: MoveFolderModalProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    if (selectedId === null) return
    setLoading(true)
    try {
      await onSelect(selectedId)
      onClose()
    } catch {
      // Error handled by caller
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rq-modal-overlay" onClick={onClose}>
      <div className="rq-modal-card" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--rq-text)]">{title}</h3>
          <button onClick={onClose} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto mb-4 -mx-2 px-2">
          {tree.map(root => (
            <TreeRow
              key={root.id}
              node={root}
              depth={0}
              selectedId={selectedId}
              excludeIds={excludeIds}
              onSelect={setSelectedId}
            />
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
            Cancel
          </button>
          <button
            disabled={selectedId === null || loading}
            onClick={handleConfirm}
            className="rq-btn-primary px-4 py-2 text-sm disabled:opacity-50"
          >
            {loading ? 'Moving...' : 'Move Here'}
          </button>
        </div>
      </div>
    </div>
  )
}
